import {
  getEventsV1,
  getEventDatesV1,
  getEventTypesV1,
  getEventCategoriesV1,
  getEventVenuesV1,
  getEventDetailV1,
  getWhatsOnEventsV1,
} from '@/services/platform/api/events/events';
import type {
  EventDateFacetDto,
  EventDetailDto,
  EventFacetDto,
  EventListItemDto,
  EventListItemDtoPagedResult,
  EventVenueFacetDto,
  GetEventCategoriesV1Params,
  GetEventDatesV1Params,
  GetEventsV1Params,
  GetEventTypesV1Params,
  GetEventVenuesV1Params,
  GetWhatsOnEventsV1Params,
} from '@/services/platform/api/model';

/**
 * Thin data-layer wrappers over the generated `api/events` client, mirroring
 * the shop `catalog` pattern: forward `Accept-Language`, unwrap `.data`, and
 * return `undefined` on any non-200 status so callers can render a fallback.
 */

/** Paged brand events for the calendar body (soonest first). */
export async function getEvents(
  params?: GetEventsV1Params,
  locale?: string,
  signal?: AbortSignal
): Promise<EventListItemDtoPagedResult | undefined> {
  const response = await getEventsV1(params, {
    headers: locale ? { 'Accept-Language': locale } : {},
    signal,
  });
  if (response.status !== 200) {
    console.warn(`getEvents: unexpected status ${response.status}`);
    return undefined;
  }
  return response.data;
}

/**
 * The next N distinct events from today, one slot per event, in chronological order — the home
 * page "What's On" rail. Not the listing: `/events` can only order by the dates SQL can see, and a
 * recurring campaign reaches us with a long-past start and no occurrence ahead of it, so it gets
 * clamped to "today" where every campaign ties and falls back to sort order. This endpoint
 * resolves each event's real next date instead. Returns a bare array, not a paged result.
 */
export async function getWhatsOn(
  params?: GetWhatsOnEventsV1Params,
  locale?: string,
  signal?: AbortSignal
): Promise<EventListItemDto[]> {
  const response = await getWhatsOnEventsV1(params, {
    headers: locale ? { 'Accept-Language': locale } : {},
    signal,
  });
  if (response.status !== 200) {
    console.warn(`getWhatsOn: unexpected status ${response.status}`);
    return [];
  }
  return response.data;
}

/** Distinct dates (Europe/Lisbon) with a per-day event count — drives the calendar rail. */
export async function getEventDates(
  params?: GetEventDatesV1Params,
  locale?: string,
  signal?: AbortSignal
): Promise<EventDateFacetDto[]> {
  const response = await getEventDatesV1(params, {
    headers: locale ? { 'Accept-Language': locale } : {},
    signal,
  });
  if (response.status !== 200) {
    console.warn(`getEventDates: unexpected status ${response.status}`);
    return [];
  }
  return response.data;
}

/** Event-type facets (value + count) for the filter pills. */
export async function getEventTypes(
  params?: GetEventTypesV1Params,
  locale?: string,
  signal?: AbortSignal
): Promise<EventFacetDto[]> {
  const response = await getEventTypesV1(params, {
    headers: locale ? { 'Accept-Language': locale } : {},
    signal,
  });
  if (response.status !== 200) {
    console.warn(`getEventTypes: unexpected status ${response.status}`);
    return [];
  }
  return response.data;
}

/** Calendar-category facets — reserved for a future category chip row. */
export async function getEventCategories(
  params?: GetEventCategoriesV1Params,
  locale?: string,
  signal?: AbortSignal
): Promise<EventFacetDto[]> {
  const response = await getEventCategoriesV1(params, {
    headers: locale ? { 'Accept-Language': locale } : {},
    signal,
  });
  if (response.status !== 200) {
    console.warn(`getEventCategories: unexpected status ${response.status}`);
    return [];
  }
  return response.data;
}

/** Full event detail by public GUID — undefined on any non-200 (including 404 or an inaccessible visibility). */
export async function getEventDetail(
  id: string,
  locale?: string,
  signal?: AbortSignal
): Promise<EventDetailDto | undefined> {
  const response = await getEventDetailV1(id, {
    headers: locale ? { 'Accept-Language': locale } : {},
    signal,
  });
  if (response.status !== 200) {
    return undefined;
  }
  return response.data;
}

/** Venue facets for the filter overlay (phase 2). */
export async function getEventVenues(
  params?: GetEventVenuesV1Params,
  locale?: string,
  signal?: AbortSignal
): Promise<EventVenueFacetDto[]> {
  const response = await getEventVenuesV1(params, {
    headers: locale ? { 'Accept-Language': locale } : {},
    signal,
  });
  if (response.status !== 200) {
    console.warn(`getEventVenues: unexpected status ${response.status}`);
    return [];
  }
  return response.data;
}
